import { useTranslation } from 'react-i18next';
import type { FillKind, Region, Term } from '@/engine';
import { RangeMatrix } from './RangeMatrix';
import { ComboCards, SUITS, SuitIcon } from './ComboCards';

/**
 * Combo cards (what A counts) beside the matrix (what B counts), above the `A × B` caption.
 * Hidden numbers keep their place (visibility only), so nothing shifts; the × always stays.
 */
export function TermBlock({
  term,
  region,
  showA = true,
  showB = true,
  fill = 'column',
  reversed = false,
  showLines = false,
}: {
  term: Term;
  region: Region;
  showA?: boolean;
  showB?: boolean;
  fill?: FillKind;
  reversed?: boolean;
  /** Card Blockers: show the term's blocked line counts above its combo grid */
  showLines?: boolean;
}) {
  return (
    <div className="enter flex flex-col items-center gap-3.5">
      <div className="flex items-end gap-5">
        <div className="flex flex-col items-center gap-2.5">
          {showLines && <BlockedLines rows={term.blockedRows} cols={term.blockedCols} />}
          <ComboCards
            a={term.a}
            side={term.side}
            blockedRows={term.blockedRows}
            blockedCols={term.blockedCols}
          />
        </div>
        <RangeMatrix
          from={term.from}
          b={term.b}
          side={term.side}
          region={region}
          rank={term.rank}
          fill={fill}
          reversed={reversed}
        />
      </div>
      {/* A and B in equal columns either side of ×, so the sign sits under the seam between the two boxes */}
      <span className="num grid w-full grid-cols-[1fr_auto_1fr] items-center gap-[0.22em] font-display text-[clamp(30px,3.6vw,52px)] leading-none font-black">
        <span className={`va text-right ${showA ? '' : 'invisible'}`}>{term.a}</span>
        <span className={`text-[0.7em] font-extrabold text-fg`}>×</span>
        <span className={`vb text-left ${showB ? '' : 'invisible'}`}>{term.b}</span>
      </span>
    </div>
  );
}

/** `+`, `−` or `=` between blocks, on the caption baseline. */
export function TermOp({ children }: { children: string }) {
  return (
    <span className="w-12 self-end max-md:self-center text-center font-display text-[clamp(30px,3.6vw,52px)] leading-none font-black text-fg">
      {children}
    </span>
  );
}

/** Two read-only pill rows (H, V) of the four suits; the suits of the term's blocked lines are lit in the blocker colour. */
function BlockedLines({
  rows,
  cols,
}: {
  rows?: readonly number[] | undefined;
  cols?: readonly number[] | undefined;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1" aria-label={t('play.blockers')}>
      {(
        [
          ['H', rows],
          ['V', cols],
        ] as const
      ).map(([key, lines]) => (
        <span key={key} className="inline-flex items-center gap-1.5">
          <span className="label w-3">{t(`play.lines${key}`)}</span>
          <span className="inline-flex gap-0.5 rounded-full border border-line-strong bg-bg p-[2px]">
            {SUITS.map((suit, i) => (
              <span
                key={suit}
                data-on={lines?.includes(i) || undefined}
                className="blocked-suit grid size-6 place-items-center rounded-full text-fg-muted data-on:bg-blocker data-on:text-fg"
              >
                <SuitIcon suit={suit} />
              </span>
            ))}
          </span>
        </span>
      ))}
    </div>
  );
}
